import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { ArrowLeft, CopySimple, Flask, TagSimple } from "@phosphor-icons/react";

export default function AdminSeoTools() {
  const { user, loading } = useAuth();
  const [dupes, setDupes] = useState(null);
  const [cuisines, setCuisines] = useState(null);
  const [q, setQ] = useState("");
  const [allTrucks, setAllTrucks] = useState([]);

  const load = async () => {
    try {
      const [d, c, p] = await Promise.all([
        api.get("/admin/seo/duplicate-trucks"),
        api.get("/admin/seo/unmapped-cuisines"),
        api.get("/profiles?limit=2000"),
      ]);
      setDupes(d.data);
      setCuisines(c.data);
      setAllTrucks(p.data?.profiles || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (user?.is_admin) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return null;
  if (!user?.is_admin) return <Navigate to="/" replace />;

  const decide = async (pair, decision) => {
    try {
      await api.post("/admin/seo/duplicate-trucks/decision", {
        truck_id_a: pair.truck_a.truck_id, truck_id_b: pair.truck_b.truck_id, decision,
      });
      toast.success(`Marked: ${decision.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const flagTest = async (truckId, name) => {
    if (!window.confirm(`Mark "${name}" as a TEST profile? It will be hidden from the directory, sitemap, and search engines (not deleted).`)) return;
    try {
      await api.put(`/admin/seo/trucks/${truckId}/test-flag`, { is_test: true });
      toast.success("Flagged as test — hidden from SEO surfaces");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-seo-tools-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={16} /> Admin
      </Link>
      <h1 className="text-2xl font-bold mb-1">SEO Tools</h1>
      <p className="text-sm text-muted-foreground mb-6">Duplicate detection and taxonomy review. Nothing here deletes or merges data automatically.</p>

      <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><Flask size={18} /> Flag a test / hidden profile</h2>
      <div className="rounded-xl border bg-card p-4 mb-10" data-testid="test-flag-tool">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search trucks by name (e.g. iOS test)…"
          className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm mb-3"
          data-testid="test-flag-search"
        />
        {q.trim() && allTrucks
          .filter((t) => (t.truck_name || "").toLowerCase().includes(q.trim().toLowerCase()))
          .slice(0, 8)
          .map((t) => (
            <div key={t.truck_id} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0 text-sm">
              <span>{t.truck_name} <span className="text-muted-foreground">/t/{t.slug || t.truck_slug || "—"}</span></span>
              <Button size="sm" variant="outline" onClick={() => flagTest(t.truck_id, t.truck_name)} data-testid={`flag-test-${t.truck_id}`}>
                <Flask size={14} className="mr-1" /> Flag as test
              </Button>
            </div>
          ))}
        <p className="text-[11px] text-muted-foreground mt-2">Flagged profiles are hidden from the directory, sitemap and search engines. The record is never deleted.</p>
      </div>

      <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><CopySimple size={18} /> Possible duplicate trucks {dupes && <span className="text-sm text-muted-foreground font-normal">({dupes.count})</span>}</h2>
      <div className="space-y-3 mb-10" data-testid="dupe-list">
        {(dupes?.pairs || []).map((p, i) => (
          <div key={i} className={`rounded-xl border bg-card p-4 ${p.decision ? "opacity-60" : ""}`}>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm font-medium">
              <span>🚚 {p.truck_a.truck_name} <span className="text-muted-foreground font-normal">/t/{p.truck_a.truck_slug}</span></span>
              <span>🚚 {p.truck_b.truck_name} <span className="text-muted-foreground font-normal">/t/{p.truck_b.truck_slug}</span></span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {p.signals.map((s) => <span key={s} className="text-[11px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{s}</span>)}
              {p.decision && <span className="text-[11px] bg-muted rounded-full px-2 py-0.5">decided: {p.decision}</span>}
            </div>
            {!p.decision && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" disabled title="Merging requires explicit approval — Phase 2">Merge (Phase 2)</Button>
                <Button size="sm" variant="outline" onClick={() => decide(p, "keep_separate")} data-testid={`dupe-keep-${i}`}>Keep separate</Button>
                <Button size="sm" variant="ghost" onClick={() => decide(p, "ignore")} data-testid={`dupe-ignore-${i}`}>Ignore</Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => flagTest(p.truck_b.truck_id, p.truck_b.truck_name)}>
                  <Flask size={14} className="mr-1" /> Flag B as test
                </Button>
              </div>
            )}
          </div>
        ))}
        {dupes && dupes.count === 0 && <p className="text-sm text-muted-foreground">No likely duplicates found.</p>}
      </div>

      <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><TagSimple size={18} /> Cuisine tags not yet in the canonical taxonomy</h2>
      <div className="rounded-xl border bg-card p-4 text-sm" data-testid="unmapped-cuisines">
        {(cuisines?.unmapped || []).map((u) => (
          <div key={u.tag} className="py-1.5 border-b border-border/50 last:border-0">
            <span className="font-medium">{u.tag}</span>
            <span className="text-muted-foreground"> — {u.trucks.slice(0, 4).join(", ")}{u.trucks.length > 4 ? ` +${u.trucks.length - 4}` : ""}</span>
          </div>
        ))}
        {cuisines && cuisines.unmapped.length === 0 && <p className="text-muted-foreground">Every owner tag maps to a canonical category. 🎉</p>}
      </div>
    </div>
  );
}
